/**
 * ExitSurvey Component
 *
 * Final exit screen - collects demographic information and participant feedback.
 * This appears after the ComprehensionQuiz screen.
 *
 * Structure:
 * - Feedback (optional, all roles) — shown first
 * - Partner question (DYAD, both roles): a mirror rating — Bob: partnerExpertise;
 *   Alice: partnerUnderstanding
 * - Experience: enjoyment (0-6), knowledgeSource (0-6), future participation interest
 * - Demographics: age, gender, education
 * - Optional feedback
 *
 * NOTE (2026-08-13): the explanation self-item (abilityToExplain /
 * partnerExplainQuality) was removed here — the single explanation question now
 * lives only in the baseline -> post-study -> post-teaching trajectory.
 *
 * Data Storage:
 * - exitSurvey: { age, gender, education, educationOther?, enjoyment, knowledgeSource,
 *                 helpful, futureParticipation, feedback,
 *                 marker, secondaryMarker,
 *                 partnerExpertise? (Bob),
 *                 partnerUnderstanding? (Alice) }
 *
 * helpful (0-6): role-adaptive - helpfulness of AI/PDF/web/partner explanation
 *
 * knowledgeSource scale (0-6): "How much did today's session help you answer the quiz?"
 *   0 = "Not at all"
 *   6 = "Very much"
 */

import { usePlayer, useGame } from "@empirica/core/player/classic/react";
import React, { useState } from "react";
import { ProgressTracker } from "@/components/feedback/ProgressTracker";

// Same stages as Profile.jsx - Final Survey is index 5
const GAME_STAGES = [
  { id: "survey", label: "Survey" },
  { id: "learning", label: "Learning" },
  { id: "preparation", label: "Preparation" },
  { id: "discussion", label: "Discussion" },
  { id: "quiz", label: "Quiz" },
  { id: "final", label: "Final Survey" },
];

export function ExitSurvey({ next }) {
  const labelClassName = "block text-sm font-medium text-gray-700 my-2";
  const inputClassName =
    "appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-empirica-500 focus:border-empirica-500 sm:text-sm";
  const player = usePlayer();
  const game = useGame();

  // Get role information
  const marker = player.get("marker");
  const secondaryMarker = player.get("secondary_marker");
  const isSingle = (game.get("gameMode") || "dyad") === "single";
  const isDyadTeacher = !isSingle && marker === 0;   // Alice
  const isDyadStudent = !isSingle && marker > 0;      // Bob

  // Role-adaptive label for the learning source (helpful question)
  const sourceLabel = marker > 0
    ? "your partner's explanation"
    : secondaryMarker === "pdf"
    ? "the PDF material"
    : secondaryMarker === "web"
    ? "the web search"
    : secondaryMarker === "ai-pdf"
    ? "the AI assistant and PDF material"
    : "the AI assistant";

  // Common demographic fields
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("");
  const [education, setEducation] = useState("");
  const [educationOther, setEducationOther] = useState("");

  // Dyad Student (Bob): perceived partner knowledgeability (expertise attribution)
  const [partnerExpertise, setPartnerExpertise] = useState("");
  // Dyad Teacher (Alice): how well the partner (student) understood by the end —
  // the mirror of the student's partnerExpertise, around the teaching direction.
  const [partnerUnderstanding, setPartnerUnderstanding] = useState("");
  // Role-selected partner rating (Bob → partner knowledge; Alice → partner understanding).
  const partnerRating = isDyadStudent ? partnerExpertise : partnerUnderstanding;
  const setPartnerRating = isDyadStudent ? setPartnerExpertise : setPartnerUnderstanding;
  const partnerRatingLabel = isDyadStudent
    ? "How knowledgeable did your partner seem?"
    : "How well do you think your partner understood the topic by the end?";
  const partnerRatingHigh = isDyadStudent ? "Very knowledgeable" : "Understood fully";

  // Source attribution: Where did your correct answers come from? (0-6 scale, ALL participants)
  // 0 = "What I already knew" (prior knowledge), 6 = "What I learned today" (learning effect)
  const [knowledgeSource, setKnowledgeSource] = useState("");

  // Task enjoyment (general question - 1-7 scale)
  const [enjoyment, setEnjoyment] = useState("");

  // helpful (0-6): how helpful was the learning source (AI/PDF/web/partner)
  const [helpful, setHelpful] = useState("");

  // Future participation interest
  const [futureParticipation, setFutureParticipation] = useState("");

  // Common feedback field
  const [feedback, setFeedback] = useState("");

  // Validation state
  const [submitted, setSubmitted] = useState(false);

  function handleSubmit(event) {
    event.preventDefault();
    setSubmitted(true);

    // Validate required fields
    const requiredFields = [
      { value: age, name: "Age" },
      { value: gender, name: "Gender" },
      { value: education, name: "Education" },
      { value: knowledgeSource, name: "Knowledge source rating" },
      { value: enjoyment, name: "Enjoyment rating" },
      { value: helpful, name: "Helpfulness rating" },
    ];

    // Partner-assessment block (dyad only): both answer how they answered the
    // quiz; Student rates partner knowledge, Teacher rates partner understanding.
    if (!isSingle) {
      requiredFields.push({
        value: partnerRating,
        name: isDyadStudent ? "Partner expertise rating" : "Partner understanding rating",
      });
    }

    // Require educationOther text if "other" is selected
    if (education === "other") {
      requiredFields.push({ value: educationOther, name: "Education (other) specification" });
    }

    const missingFields = requiredFields.filter(f => {
      // Numbers (0-6 scales): empty string means not selected, 0 is valid
      if (typeof f.value === 'number') return false; // number means selected
      // Strings: check for empty or whitespace-only
      if (typeof f.value === 'string') return f.value.trim() === "";
      // Anything else (null, undefined): missing
      return !f.value;
    });

    if (missingFields.length > 0) {
      return; // Don't submit if required fields are missing
    }

    player.set("exitSurvey", {
      age,
      gender,
      education,
      ...(education === "other" && { educationOther }),
      feedback,
      marker,
      secondaryMarker,
      knowledgeSource,
      enjoyment,
      helpful,
      futureParticipation,
      // Partner-assessment mirror rating (dyad only): Student rates partner
      // knowledge, Teacher rates partner understanding (around the teaching direction).
      ...(isDyadStudent && { partnerExpertise }),
      ...(isDyadTeacher && { partnerUnderstanding }),
    });
    next();
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Progress Tracker */}
      <div className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm border-b">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <ProgressTracker stages={GAME_STAGES} currentIndex={5} />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Header Card */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-6 mb-8 text-white shadow-lg">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold">Almost Done!</h1>
          </div>
          <p className="text-indigo-100 text-sm">
            Just a few quick questions and you'll receive your completion code.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Feedback (optional) — shown first */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-amber-50 px-5 py-3 border-b border-amber-100">
              <h2 className="font-semibold text-amber-900 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                </svg>
                Feedback
                <span className="text-xs font-normal text-amber-700">(optional)</span>
              </h2>
            </div>
            <div className="p-5">
              <textarea
                className={inputClassName}
                rows={3}
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Any comments, suggestions, or issues you encountered..."
              />
            </div>
          </div>

          {/* Partner Questions - DYAD ONLY (both roles; mirror around the teaching direction) */}
          {!isSingle && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-blue-50 px-5 py-3 border-b border-blue-100">
                <h2 className="font-semibold text-blue-900 flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  About Your Partner
                </h2>
              </div>
              <div className="p-5 space-y-5">
                <div>
                  <label className={labelClassName}>
                    {partnerRatingLabel} <span className="text-red-500">*</span>
                  </label>
                  <div className={`flex items-center justify-between mt-2 p-3 rounded-lg bg-gray-50 ${submitted && partnerRating === "" ? 'ring-2 ring-red-300' : ''}`}>
                    <span className="text-xs text-gray-500 w-20">Not at all</span>
                    <div className="flex gap-1">
                      {[0, 1, 2, 3, 4, 5, 6].map((v) => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => setPartnerRating(v)}
                          className={`w-10 h-10 rounded-lg font-medium text-sm transition-all ${
                            partnerRating === v
                              ? "bg-blue-600 text-white shadow-md scale-110"
                              : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
                          }`}
                        >
                          {v}
                        </button>
                      ))}
                    </div>
                    <span className="text-xs text-gray-500 w-20 text-right">{partnerRatingHigh}</span>
                  </div>
                  {submitted && partnerRating === "" && (
                    <p className="text-red-500 text-xs mt-1">Please select a rating</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Experience & Future */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-purple-50 px-5 py-3 border-b border-purple-100">
              <h2 className="font-semibold text-purple-900 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Your Experience
              </h2>
            </div>
            <div className="p-5 space-y-5">
              <ScaleQuestion
                label={`How helpful was ${sourceLabel} for learning about the topic?`}
                lowLabel="Not at all"
                highLabel="Very helpful"
                value={helpful}
                onChange={setHelpful}
                submitted={submitted}
                labelClassName={labelClassName}
              />

              <div>
                <label className={labelClassName}>
                  How enjoyable did you find this task? <span className="text-red-500">*</span>
                </label>
                <div className={`flex items-center justify-between mt-2 p-3 rounded-lg bg-gray-50 ${submitted && enjoyment === "" ? 'ring-2 ring-red-300' : ''}`}>
                  <span className="text-xs text-gray-500 w-20">Not at all</span>
                  <div className="flex gap-1">
                    {[0, 1, 2, 3, 4, 5, 6].map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setEnjoyment(v)}
                        className={`w-10 h-10 rounded-lg font-medium text-sm transition-all ${
                          enjoyment === v
                            ? "bg-purple-600 text-white shadow-md scale-110"
                            : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
                        }`}
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                  <span className="text-xs text-gray-500 w-20 text-right">Very enjoyable</span>
                </div>
                {submitted && enjoyment === "" && (
                  <p className="text-red-500 text-xs mt-1">Please select a rating</p>
                )}
              </div>

              <div>
                <label className={labelClassName}>
                  How much did today's session help you answer the quiz? <span className="text-red-500">*</span>
                </label>
                <div className={`flex items-center justify-between mt-2 p-3 rounded-lg bg-gray-50 ${submitted && knowledgeSource === "" ? 'ring-2 ring-red-300' : ''}`}>
                  <span className="text-xs text-gray-500 w-20">Not at all</span>
                  <div className="flex gap-1">
                    {[0, 1, 2, 3, 4, 5, 6].map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setKnowledgeSource(v)}
                        className={`w-10 h-10 rounded-lg font-medium text-sm transition-all ${
                          knowledgeSource === v
                            ? "bg-purple-600 text-white shadow-md scale-110"
                            : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
                        }`}
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                  <span className="text-xs text-gray-500 w-20 text-right">Very much</span>
                </div>
                {submitted && knowledgeSource === "" && (
                  <p className="text-red-500 text-xs mt-1">Please select a rating</p>
                )}
              </div>

              <div>
                <label className={labelClassName}>
                  Interested in future experiments?
                </label>
                <div className="flex gap-3 mt-2">
                  {["yes", "no"].map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setFutureParticipation(value)}
                      className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                        futureParticipation === value
                          ? "bg-green-600 text-white shadow-md"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      {value === "yes" ? "Yes, sign me up!" : "No thanks"}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Demographics */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-5 py-3 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Demographics
              </h2>
            </div>
            <div className="p-5 space-y-5">
              <div className="grid grid-cols-2 gap-5">
                <div>
                  <label htmlFor="age" className={labelClassName}>
                    Age <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="age"
                    name="age"
                    type="number"
                    min="18"
                    max="120"
                    autoComplete="off"
                    className={`${inputClassName} ${submitted && !age ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                    placeholder="Your age"
                  />
                  {submitted && !age && (
                    <p className="text-red-500 text-xs mt-1">Required</p>
                  )}
                </div>

                <div>
                  <label className={labelClassName}>
                    Gender <span className="text-red-500">*</span>
                  </label>
                  <div className={`flex flex-wrap gap-2 ${submitted && !gender ? 'p-2 rounded-lg ring-2 ring-red-300' : ''}`}>
                    {[
                      { value: "male", label: "Male" },
                      { value: "female", label: "Female" },
                      { value: "other", label: "Other" },
                      { value: "prefer-not-to-say", label: "Prefer not to say" }
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setGender(opt.value)}
                        className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                          gender === opt.value
                            ? "bg-indigo-600 text-white"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  {submitted && !gender && (
                    <p className="text-red-500 text-xs mt-1">Please select an option</p>
                  )}
                </div>
              </div>

              <div>
                <label className={labelClassName}>
                  Highest Education Level <span className="text-red-500">*</span>
                </label>
                <div className={`grid grid-cols-2 gap-2 ${submitted && !education ? 'p-2 rounded-lg ring-2 ring-red-300' : ''}`}>
                  {[
                    { value: "high-school-or-less", label: "High school or less" },
                    { value: "some-college", label: "Some college" },
                    { value: "associates", label: "Associate's" },
                    { value: "bachelors", label: "Bachelor's" },
                    { value: "masters", label: "Master's" },
                    { value: "professional", label: "Professional (MD, JD)" },
                    { value: "doctoral", label: "Doctoral (PhD)" },
                    { value: "other", label: "Other" }
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setEducation(opt.value)}
                      className={`px-3 py-2 rounded-lg text-sm transition-all text-left ${
                        education === opt.value
                          ? "bg-indigo-600 text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {submitted && !education && (
                  <p className="text-red-500 text-xs mt-1">Please select an option</p>
                )}
                {education === "other" && (
                  <div className="mt-3">
                    <input
                      type="text"
                      className={`${inputClassName} ${submitted && education === "other" && !educationOther ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                      value={educationOther}
                      onChange={(e) => setEducationOther(e.target.value)}
                      placeholder="Please specify your education level"
                    />
                    {submitted && education === "other" && !educationOther && (
                      <p className="text-red-500 text-xs mt-1">Please specify</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full py-4 px-6 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold rounded-xl shadow-lg transition-all duration-200 flex items-center justify-center gap-2"
          >
            <span>Complete & Get Code</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </button>
        </form>
      </div>
    </div>
  );
}

/**
 * Reusable 0-6 rating scale question (matches the survey's button style)
 */
function ScaleQuestion({ label, lowLabel, highLabel, value, onChange, submitted, labelClassName }) {
  return (
    <div>
      <label className={labelClassName}>
        {label} <span className="text-red-500">*</span>
      </label>
      <div className={`flex items-center justify-between mt-2 p-3 rounded-lg bg-gray-50 ${submitted && value === "" ? 'ring-2 ring-red-300' : ''}`}>
        <span className="text-xs text-gray-500 w-20">{lowLabel}</span>
        <div className="flex gap-1">
          {[0, 1, 2, 3, 4, 5, 6].map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => onChange(v)}
              className={`w-10 h-10 rounded-lg font-medium text-sm transition-all ${
                value === v
                  ? "bg-purple-600 text-white shadow-md scale-110"
                  : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
              }`}
            >
              {v}
            </button>
          ))}
        </div>
        <span className="text-xs text-gray-500 w-20 text-right">{highLabel}</span>
      </div>
      {submitted && value === "" && (
        <p className="text-red-500 text-xs mt-1">Please select a rating</p>
      )}
    </div>
  );
}